import React from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { MATERIAL_ICON_OPTIONS, POPULAR_MATERIAL_ICON_OPTIONS, getMaterialIconComponent } from "./materialIcons";

const GERMAN_ICON_SEARCH_ALIASES = {
    abzeichen: ["badge", "verified"],
    adresse: ["location", "place", "map"],
    aktiv: ["check", "done", "verified"],
    apotheke: ["localpharmacy", "medical"],
    auszeichnung: ["award", "emoji", "events", "grade", "star", "workspace"],
    auto: ["car", "directionscar", "garage"],
    autobahn: ["car", "traffic", "directionscar"],
    bahnhof: ["train", "tram", "railway"],
    bank: ["accountbalance", "savings"],
    bar: ["localbar", "liquor"],
    bearbeiten: ["edit", "draw"],
    berg: ["terrain", "landscape"],
    bett: ["bed", "hotel", "bedroom"],
    bewertung: ["star", "grade", "rating"],
    bezahlen: ["payment", "creditcard", "payments"],
    bild: ["image", "photo", "collections"],
    bus: ["bus", "directionsbus"],
    camping: ["terrain", "camping", "forest"],
    check: ["check", "done"],
    essen: ["restaurant", "dining", "food", "lunch", "dinner", "ramen"],
    fahrrad: ["bike", "bicycle", "directionsbike", "electricbike"],
    familie: ["family", "groups", "people"],
    ferienhaus: ["holiday", "house", "cottage", "villa"],
    fisch: ["setmeal", "restaurant"],
    fitness: ["fitness", "sports", "gym"],
    flughafen: ["flight", "airport"],
    flugzeug: ["flight", "airplanemode"],
    freizeit: ["sports", "sunny", "park", "attractions"],
    frühstück: ["breakfast", "brunch", "coffee", "bakery"],
    gebäude: ["business", "apartment", "domain", "locationcity"],
    geld: ["money", "payments", "euro", "attachmoney"],
    getränk: ["localbar", "liquor", "coffee"],
    golf: ["golf"],
    haus: ["house", "home", "cottage", "villa"],
    hotel: ["hotel", "bed"],
    hund: ["pets"],
    icon: ["icon"],
    karte: ["map", "menu", "restaurantmenu"],
    kontakt: ["contact", "phone", "mail"],
    küche: ["kitchen", "restaurant", "dining"],
    laden: ["store", "shop"],
    lage: ["location", "place", "pin", "map"],
    löschen: ["delete", "remove"],
    mail: ["mail", "email"],
    meer: ["water", "waves", "sailing"],
    motorrad: ["motorcycle", "sportsmotorsports"],
    natur: ["nature", "forest", "park", "eco"],
    parken: ["localparking", "garage"],
    parkplatz: ["localparking", "garage"],
    person: ["person", "accountcircle"],
    pfeil: ["arrow"],
    qualität: ["verified", "check", "workspacepremium"],
    restaurant: ["restaurant", "dining", "localdining"],
    reiten: ["sportskabaddi"],
    schiff: ["boat", "sailing", "directionsboat"],
    schlafen: ["bed", "hotel"],
    schwimmen: ["pool", "water"],
    see: ["water", "waves", "sailing"],
    sonne: ["sunny", "wbsunny", "lightmode"],
    spa: ["spa", "selfimprovement"],
    speichern: ["save"],
    spielplatz: ["park", "attractions", "childcare"],
    sport: ["sports", "fitness"],
    standort: ["location", "place", "pin", "map"],
    stern: ["star", "grade"],
    suche: ["search"],
    tag: ["localoffer", "sell", "label"],
    telefon: ["phone", "call"],
    tennis: ["sportstennis"],
    trophäe: ["emoji events", "workspacepremium", "militarytech"],
    zug: ["train", "tram"],
};

function normalize(value) {
    return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ß/g, "ss");
}

function expandGermanQuery(query) {
    const q = normalize(query);
    if (!q) return [""];

    const expansions = new Set([q]);
    Object.entries(GERMAN_ICON_SEARCH_ALIASES).forEach(([german, englishTerms]) => {
        const germanNorm = normalize(german);
        if (germanNorm.includes(q) || q.includes(germanNorm)) {
            englishTerms.forEach((term) => expansions.add(normalize(term)));
        }
    });
    return Array.from(expansions);
}

export default function MaterialIconPicker({
    label = "MaterialUI Icon",
    value,
    onChange,
    options = MATERIAL_ICON_OPTIONS,
}) {
    const selected =
        options.find((option) => option.value === (value || "")) ||
        (value ? { value, label: value, Icon: getMaterialIconComponent(value) } : options[0]);

    return (
        <Autocomplete
            options={options}
            value={selected}
            onChange={(_event, option) => onChange?.(option?.value || "")}
            autoHighlight
            disableClearable
            isOptionEqualToValue={(option, selectedValue) => option.value === selectedValue.value}
            getOptionLabel={(option) => option?.label || ""}
            filterOptions={(items, state) => {
                const q = state.inputValue.trim();
                if (q.length < 2) {
                    return POPULAR_MATERIAL_ICON_OPTIONS;
                }
                const terms = expandGermanQuery(q);
                return items.filter((option) => {
                    if (option.value === "") return true;
                    const haystack = normalize(option.searchText || `${option.label} ${option.value}`);
                    return terms.some((term) => haystack.includes(term));
                }).slice(0, 160);
            }}
            noOptionsText="Bitte mindestens 2 Zeichen suchen"
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    helperText="Tippe mindestens 2 Zeichen oder öffne die Liste für häufige Icons."
                />
            )}
            renderOption={(props, option) => {
                const { key, ...rest } = props;
                const Icon = option.Icon;
                return (
                    <Box component="li" key={key} {...rest} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {Icon ? <Icon fontSize="small" /> : <Box sx={{ width: 20 }} />}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap>{option.label}</Typography>
                            {option.value && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {option.value}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            }}
            renderValue={(option) => {
                const Icon = option?.Icon;
                return (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        {Icon && <Icon fontSize="small" />}
                        <Typography component="span" noWrap>{option?.label || "Kein Icon"}</Typography>
                    </Box>
                );
            }}
        />
    );
}
